import { Component, computed, inject } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { SubmitProposalStateService } from "@app/features/submit-proposal/services/submit-proposal-state.service";
import { StatusMessageComponent } from "@app/shared/components/status-message.component";
import { Constants } from "@core/constants";
import { PlanHelper } from "@core/domain/helpers/plan-helper";
import { IonButton, IonIcon, IonTextarea } from "@ionic/angular/standalone";
import { TranslatePipe } from "@ngx-translate/core";
import {
  GenericDataListItem,
  GenericDataTableComponent,
} from "@shared/components/generic-data-table/generic-data-table.component";
import { PlanStateService } from "@shared/services/plan-state.service";

@Component({
  selector: "pa-submit-proposal-left-confirm",
  templateUrl: "./submit-proposal-left-confirm.component.html",
  styleUrls: ["./submit-proposal-left-confirm.component.scss"],
  imports: [
    GenericDataTableComponent,
    FormsModule,
    StatusMessageComponent,
    TranslatePipe,
    IonTextarea,
    IonButton,
    IonIcon,
  ],
})
export class SubmitProposalLeftConfirmComponent {
  private readonly submitProposalStateService = inject(SubmitProposalStateService);
  private readonly planStateService = inject(PlanStateService);
  private readonly planHelper = inject(PlanHelper);

  protected readonly explanationMaxLength = Constants.EXPLANATION_MAX_LENGTH;
  protected uploadBtnDisabled = false;
  protected selectedFiles: File[] = this.submitProposalStateService.filesToUpload.map((fileInfo) => fileInfo.file);
  protected studentExplanation = this.submitProposalStateService.studentExplanation;
  protected submitInfo = this.submitProposalStateService.submitInfo;
  protected messages = this.submitProposalStateService.messages;
  protected dataList = computed<GenericDataListItem[]>(() => {
    // Re-initialize the dataList when the submitInfo changes
    const submitInfo = this.submitInfo();
    this.submitProposalStateService.determineSubmitDisabled();
    return submitInfo ? this.submitProposalStateService.buildInfoTable() : [];
  });

  isUploadPossible(): boolean {
    return this.planHelper.isUploadPossible(this.planStateService.currentSelectedPlan()!);
  }

  onFileChange(fileChangeEvent: Event): void {
    const fileInputElement: HTMLInputElement = fileChangeEvent.target as HTMLInputElement;
    const files: FileList | null = fileInputElement.files;
    if (files) {
      this.addToSelectedFiles(Array.from(files));
      this.submitProposalStateService.setFilesToUpload(this.selectedFiles);
    }
  }

  openFile(file: File): void {
    const fileURL = URL.createObjectURL(file);
    window.open(fileURL);
  }

  removeFile(index: number): void {
    this.selectedFiles.splice(index, 1);
    this.submitProposalStateService.setFilesToUpload(this.selectedFiles);
  }

  isUploadRequired(): boolean {
    return this.submitProposalStateService.isUploadDocumentRequired();
  }

  maxUploadCountReached(): boolean {
    return this.selectedFiles.length >= this.submitInfo()!.maxDocumentsToSubmit;
  }

  onStudentExplanationChange(): void {
    this.submitProposalStateService.studentExplanation = this.studentExplanation;
    this.submitProposalStateService.determineSubmitDisabled();
  }

  private addToSelectedFiles(files: File[]) {
    const selectedFiles = [...this.selectedFiles, ...files];

    // Make sure that the selectedFiles array contains only files with unique names
    this.selectedFiles = [...new Map(selectedFiles.map((item) => [item.name, item])).values()];
  }
}
